import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Eye, Filter, CheckCircle, Clock, FileText, ThumbsUp, Unlock, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

const ViewDayClosings = ({ user }) => {
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [selectedClosing, setSelectedClosing] = useState(null);
  const [deductions, setDeductions] = useState([]);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  
  // Loading states to prevent double-click
  const [approvingId, setApprovingId] = useState(null);
  const [unlockingId, setUnlockingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchClosings = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dayclosings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = response.data;
      if (user.role === 'accountant') {
        data = data.filter(c => c.status === 'closed' || c.status === 'approved');
      }
      setClosings(data);
      setLastRefresh(new Date());
    } catch (error) {
      toast.error('Failed to fetch day closings');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user.role]);

  useEffect(() => {
    fetchClosings();
  }, [fetchClosings]);

  // Auto-refresh every 45 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchClosings(true), 45000);
    return () => clearInterval(interval);
  }, [fetchClosings]);

  const handleManualRefresh = () => {
    fetchClosings(true);
    toast.success('Data refreshed!');
  };

  const fetchDeductions = async (closingId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dayclosings/${closingId}/deductions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeductions(response.data);
    } catch (error) {
      toast.error('Failed to fetch deductions');
    }
  };

  const openDetails = async (closing) => {
    setSelectedClosing(closing);
    await fetchDeductions(closing.id);
    setDetailsDialog(true);
  };

  const handleApprove = async (closingId) => {
    if (approvingId) return; // Prevent double-click
    
    try {
      setApprovingId(closingId);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/dayclosings/${closingId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Day closing approved successfully');
      fetchClosings();
      setDetailsDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve day closing'));
    } finally {
      setApprovingId(null);
    }
  };

  const handleUnlock = async (closingId) => {
    if (unlockingId) return; // Prevent double-click
    
    try {
      setUnlockingId(closingId);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/dayclosings/${closingId}/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Day closing unlocked for editing');
      fetchClosings();
      setDetailsDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to unlock day closing'));
    } finally {
      setUnlockingId(null);
    }
  };

  const handleDelete = async (closingId) => {
    if (deletingId) return; // Prevent double-click
    if (!window.confirm('Are you sure you want to delete this day closing? This action cannot be undone.')) {
      return;
    }
    
    try {
      setDeletingId(closingId);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dayclosings/${closingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Day closing deleted successfully');
      fetchClosings();
      setDetailsDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete day closing'));
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" />Draft</Badge>;
      case 'closed':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300"><FileText className="w-3 h-3 mr-1" />Submitted</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Filter closings
  const filteredClosings = closings.filter(c => {
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    if (filterDate && c.date !== filterDate) return false;
    return true;
  });

  // Calculate totals
  const totalSales = filteredClosings.reduce((sum, c) => sum + c.total_sales, 0);
  const totalDeductions = filteredClosings.reduce((sum, c) => sum + c.total_deductions, 0);

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="view-closings-page">
      {/* Refresh Bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Last updated: {lastRefresh.toLocaleTimeString('en-IN')}
          {refreshing && <span className="ml-2 text-blue-500">• Refreshing...</span>}
        </p>
        <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={refreshing} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Records</p>
              <p className="text-3xl font-bold text-blue-600" style={{ fontFamily: 'Space Grotesk' }}>
                {filteredClosings.length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Sales</p>
              <p className="text-3xl font-bold text-green-600" style={{ fontFamily: 'Space Grotesk' }}>
                ₹{totalSales.toLocaleString('en-IN')}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Deductions</p>
              <p className="text-3xl font-bold text-red-600" style={{ fontFamily: 'Space Grotesk' }}>
                ₹{totalDeductions.toLocaleString('en-IN')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Day Closings Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Calendar className="w-5 h-5" />
            Day Closings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Filters:</span>
            </div>
            <Input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-40"
              placeholder="Filter by date"
            />
            {user.role === 'admin' && (
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="closed">Submitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                </SelectContent>
              </Select>
            )}
            {(filterDate || filterStatus !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setFilterDate(''); setFilterStatus('all'); }}
              >
                Clear Filters
              </Button>
            )}
          </div>

          {filteredClosings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No day closings found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Cashier</TableHead>
                  <TableHead>Outlet</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total Sales</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Closing Cash</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClosings.map((closing) => (
                  <TableRow key={closing.id}>
                    <TableCell className="font-medium">
                      {new Date(closing.date).toLocaleDateString('en-IN', { 
                        day: '2-digit', 
                        month: 'short', 
                        year: 'numeric' 
                      })}
                    </TableCell>
                    <TableCell>{closing.cashier_name}</TableCell>
                    <TableCell>{closing.outlet || '-'}</TableCell>
                    <TableCell>{getStatusBadge(closing.status)}</TableCell>
                    <TableCell className="text-right text-green-600 font-medium">
                      ₹{closing.total_sales.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right text-red-600 font-medium">
                      ₹{closing.total_deductions.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      ₹{closing.closing_cash.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetails(closing)}
                          data-testid={`view-closing-${closing.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {user.role === 'admin' && closing.status === 'closed' && (
                          <Button
                            variant="default"
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(closing.id)}
                            data-testid={`approve-closing-${closing.id}`}
                          >
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        )}
                        {user.role === 'admin' && (closing.status === 'closed' || closing.status === 'approved') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            onClick={() => handleUnlock(closing.id)}
                            data-testid={`unlock-closing-${closing.id}`}
                          >
                            <Unlock className="w-4 h-4 mr-1" />
                            Unlock
                          </Button>
                        )}
                        {user.role === 'admin' && closing.status === 'draft' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleDelete(closing.id)}
                            data-testid={`delete-closing-${closing.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>
              Day Closing Details - {selectedClosing?.date}
            </DialogTitle>
          </DialogHeader>
          
          {selectedClosing && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Cashier</p>
                  <p className="font-medium">{selectedClosing.cashier_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(selectedClosing.status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Opening Cash</p>
                  <p className="font-medium">₹{selectedClosing.opening_cash.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cash Received from Bank</p>
                  <p className="font-medium text-blue-600">₹{(selectedClosing.cash_received || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Gross Sales</p>
                  <p className="font-medium">₹{selectedClosing.gross_sales.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Extra Liquor Sale</p>
                  <p className="font-medium">₹{selectedClosing.extra_liquor_sale.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Extra Other Sale</p>
                  <p className="font-medium">₹{selectedClosing.extra_other_sale.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">NC Amount</p>
                  <p className="font-medium text-purple-600">₹{(selectedClosing.nc_amount || 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Discounts Given</p>
                  <p className="font-medium">₹{(selectedClosing.discounts_given || 0).toLocaleString('en-IN')}</p>
                </div>
                <div className="col-span-2 pt-2 border-t">
                  <p className="text-sm text-gray-500">Total Sales</p>
                  <p className="font-bold text-green-600 text-lg">₹{selectedClosing.total_sales.toLocaleString('en-IN')}</p>
                </div>
              </div>

              {/* Deductions */}
              <div>
                <h3 className="font-semibold mb-3">Deductions</h3>
                {deductions.length === 0 ? (
                  <p className="text-gray-500 text-sm">No deductions</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Remarks</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deductions.map((d) => (
                        <TableRow key={d.id}>
                          <TableCell className="font-medium">{d.category}</TableCell>
                          <TableCell className="text-gray-600">{d.remarks || '-'}</TableCell>
                          <TableCell className="text-right text-red-600">₹{d.amount.toLocaleString('en-IN')}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-red-50 font-bold">
                        <TableCell colSpan={2}>Total Deductions</TableCell>
                        <TableCell className="text-right text-red-700">
                          ₹{selectedClosing.total_deductions.toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Final Summary */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-blue-800">Closing Cash</span>
                  <span className="text-2xl font-bold text-blue-800" style={{ fontFamily: 'Space Grotesk' }}>
                    ₹{selectedClosing.closing_cash.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {selectedClosing.notes && (
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800 font-medium">Notes:</p>
                  <p className="text-yellow-700">{selectedClosing.notes}</p>
                </div>
              )}

              {/* Action Buttons for Admin */}
              {user.role === 'admin' && (
                <div className="flex justify-end gap-2 pt-4 border-t">
                  {selectedClosing.status === 'closed' && (
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(selectedClosing.id)}
                      data-testid="approve-closing-dialog-btn"
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      Approve Day Closing
                    </Button>
                  )}
                  {(selectedClosing.status === 'closed' || selectedClosing.status === 'approved') && (
                    <Button
                      variant="outline"
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      onClick={() => handleUnlock(selectedClosing.id)}
                      data-testid="unlock-closing-dialog-btn"
                    >
                      <Unlock className="w-4 h-4 mr-2" />
                      Unlock for Editing
                    </Button>
                  )}
                  {selectedClosing.status === 'draft' && (
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => handleDelete(selectedClosing.id)}
                      data-testid="delete-closing-dialog-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Day Closing
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ViewDayClosings;
